import React, { useRef, useState, useEffect, useCallback } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { Zap, Package, Truck, ArrowUpRight, FileText, Route, PackageCheck, MonitorDot, MapPin, CheckCircle2, Leaf, Wrench, Lock, ThermometerSnowflake, Maximize2, X, ChevronLeft, ChevronRight } from "lucide-react";
import { useLang, BRAND_IMAGES, GALLERY } from "@/lib/i18n";
import { Reveal, SectionLabel, scrollToId } from "@/lib/ui";

const FLEET = [
  { key: "curtain", img: "/poza14.webp", pos: "object-[center_65%]", icon: Truck, ro: "Curtain Side", en: "Curtain Side", dro: "Prelată laterală pentru încărcare rapidă și marfă paletizată.", den: "Side curtain for fast loading and palletized cargo.", span: "lg:col-span-2" },
  { key: "express", img: "/poza10.webp", pos: "object-center", icon: Zap, ro: "Express", en: "Express", dro: "Livrări rapide, dedicate, cu termene stricte.", den: "Fast, dedicated deliveries with strict deadlines.", span: "" },
  { key: "cargo", img: "/poza4.webp", pos: "object-[center_50%]", icon: Package, ro: "General Cargo", en: "General Cargo", dro: "Soluții flexibile pentru orice tip de marfă generală.", den: "Flexible solutions for any type of general cargo.", span: "lg:col-span-3" },
];

export function Fleet() {
  const { t } = useLang();
  return (
    <section id="flota" className="relative py-24 md:py-32 bg-mesh noise">
      <div className="relative z-10 mx-auto max-w-7xl px-5 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-2xl">
            <SectionLabel>{t({ ro: "Flotă diversificată", en: "Diverse fleet" })}</SectionLabel>
            <Reveal>
              <h2 className="mt-5 font-display text-4xl md:text-5xl font-extrabold tracking-tighter">
                {t({ ro: "Cu ce transportăm", en: "What we transport with" })}
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <p className="max-w-sm text-slate-400">
              {t({ ro: "Flotă tânără, întreținută riguros, echipată cu tehnologie de ultimă generație și normă EURO 6.", en: "A young, strictly maintained fleet equipped with state-of-the-art technology and EURO 6 standard." })}
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-5">
          {FLEET.map((f, i) => {
            const Icon = f.icon;
            return (
              <Reveal key={f.key} delay={i * 0.08} className={f.span}>
                <div data-testid={`fleet-card-${f.key}`} className="group relative aspect-[4/5] sm:aspect-auto sm:h-[420px] md:h-[460px] overflow-hidden rounded-[1.75rem] border border-white/10">
                  <img src={f.img} alt={t(f)} className={`absolute inset-0 h-full w-full object-cover ${f.pos} transition-transform duration-700 ease-out group-hover:scale-110`} />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/50 to-slate-950/10" />
                  <div className="absolute top-5 left-5 grid h-12 w-12 place-items-center rounded-xl glass">
                    <Icon className="h-6 w-6 text-sky-400" />
                  </div>
                  <ArrowUpRight className="absolute top-6 right-6 h-6 w-6 text-white/60 -translate-y-1 opacity-0 group-hover:opacity-100 group-hover:translate-y-0 transition-all" />
                  <div className="absolute bottom-0 inset-x-0 p-7">
                    <h3 className="font-display text-2xl font-bold">{t(f)}</h3>
                    <p className="mt-2 max-w-md text-slate-300/90">{t({ ro: f.dro, en: f.den })}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------- Gallery (fleet photos) ---------- */
function GalleryLightbox({ index, onClose, onPrev, onNext }) {
  const { t } = useLang();
  const g = GALLERY[index];

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onPrev, onNext]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-slate-950/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        aria-label="Close"
        className="absolute top-5 right-5 grid h-11 w-11 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
      >
        <X className="h-5 w-5" />
      </button>

      <button
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        aria-label="Previous"
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 grid h-11 w-11 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
      >
        <ChevronLeft className="h-6 w-6" />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        aria-label="Next"
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 grid h-11 w-11 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
      >
        <ChevronRight className="h-6 w-6" />
      </button>

      <motion.div
        key={index}
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.25 }}
        className="relative max-w-5xl w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <img src={g.url} alt={t(g)} className="w-full max-h-[80vh] object-contain rounded-2xl" />
        <div className="mt-4 text-center">
          <span className="text-xs uppercase tracking-[0.2em] text-sky-400">HEB TEAM TRANS</span>
          <h3 className="mt-1 font-display text-xl font-bold text-white">{t(g)}</h3>
        </div>
      </motion.div>
    </motion.div>
  );
}

export function Gallery() {
  const { t } = useLang();
  const [openIndex, setOpenIndex] = useState(null);

  const close = useCallback(() => setOpenIndex(null), []);
  const prev = useCallback(() => setOpenIndex((i) => (i - 1 + GALLERY.length) % GALLERY.length), []);
  const next = useCallback(() => setOpenIndex((i) => (i + 1) % GALLERY.length), []);

  return (
    <section className="relative py-16 md:py-24 bg-slate-950">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="max-w-2xl">
          <SectionLabel>{t({ ro: "Galerie flotă", en: "Fleet gallery" })}</SectionLabel>
          <Reveal>
            <h2 className="mt-5 font-display text-4xl md:text-5xl font-extrabold tracking-tighter">
              {t({ ro: "Flota noastră în acțiune", en: "Our fleet in action" })}
            </h2>
          </Reveal>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-5">
          {GALLERY.map((g, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <button
                type="button"
                data-testid={`gallery-item-${i}`}
                onClick={() => setOpenIndex(i)}
                className="group relative block w-full h-[300px] md:h-[380px] overflow-hidden rounded-[1.75rem] border border-white/10 text-left cursor-zoom-in"
              >
                <img src={g.url} alt={t(g)} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/20 to-transparent" />
                <div className="absolute top-5 right-5 grid h-10 w-10 place-items-center rounded-full glass opacity-0 group-hover:opacity-100 transition-opacity">
                  <Maximize2 className="h-4 w-4 text-white" />
                </div>
                <div className="absolute bottom-0 inset-x-0 p-6 translate-y-2 opacity-90 group-hover:translate-y-0 transition-transform">
                  <span className="text-xs uppercase tracking-[0.2em] text-sky-400">HEB TEAM TRANS</span>
                  <h3 className="mt-1 font-display text-xl font-bold">{t(g)}</h3>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {openIndex !== null && (
          <GalleryLightbox index={openIndex} onClose={close} onPrev={prev} onNext={next} />
        )}
      </AnimatePresence>
    </section>
  );
}

/* ---------- Fleet Premium (technical spec block) ---------- */
const STD_BRANDS = ["MAN", "Scania", "Volvo"];
const TRL_BRANDS = ["Schmitz", "Kögel", "Krone"];

function BrandRow({ brands }) {
  return (
    <div className="mt-6 pt-5 border-t border-white/10 flex items-center justify-around gap-3">
      {brands.map((name) => (
        <div key={name} className="grid h-16 flex-1 place-items-center rounded-lg bg-white/95 px-3">
          <img src={BRAND_IMAGES[name]} alt={name} className="max-h-11 w-auto max-w-full object-contain" />
        </div>
      ))}
    </div>
  );
}

function TechCard({ icon: Icon, title, sub, specs, brands, tid }) {
  return (
    <div data-testid={tid} className="h-full rounded-[1.75rem] border border-white/10 bg-slate-900/50 p-7 hover:border-sky-400/40 transition-colors">
      <div className="flex items-center gap-4">
        <div className="grid h-12 w-12 place-items-center rounded-xl bg-blue-600 shrink-0"><Icon className="h-6 w-6 text-white" /></div>
        <div>
          <h3 className="font-display text-xl font-bold tracking-tight">{title}</h3>
          <span className="text-sm text-slate-400">{sub}</span>
        </div>
      </div>
      <div className="mt-6 grid sm:grid-cols-2 gap-3">
        {specs.map((s, i) => {
          const SI = s.icon;
          return (
            <div key={i} className="flex items-start gap-3 rounded-xl border border-white/10 bg-slate-950/60 p-4">
              <SI className="h-5 w-5 text-sky-400 shrink-0 mt-0.5" />
              <div>
                <strong className="block text-sm font-semibold">{s.t}</strong>
                <p className="text-xs text-slate-400 mt-0.5">{s.d}</p>
              </div>
            </div>
          );
        })}
      </div>
      <BrandRow brands={brands} />
    </div>
  );
}

export function FleetPremium() {
  const { t } = useLang();
  const stats = [
    { v: "EURO6", l: t({ ro: "Normă poluare", en: "Emission standard" }) },
    { v: "24/7", l: t({ ro: "Monitorizare GPS", en: "GPS tracking" }) },
    { v: "100%", l: t({ ro: "Verificare tehnică", en: "Technical inspection" }) },
  ];
  return (
    <section className="relative py-16 md:py-20 bg-slate-950">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        {/* Intro card */}
        <Reveal className="rounded-[2rem] border border-blue-500/20 bg-blue-600/[0.06] p-8 md:p-10">
          <div className="grid lg:grid-cols-[1fr_auto] gap-8 items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-sky-400/30 bg-sky-400/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-sky-300">
                <span className="h-1.5 w-1.5 rounded-full bg-sky-400 node-pulse" />
                {t({ ro: "Transport internațional", en: "International transport" })}
              </span>
              <h3 className="mt-5 font-display text-2xl md:text-3xl font-extrabold tracking-tight">
                {t({ ro: "FLOTĂ PREMIUM & ÎNTREȚINERE RIGUROASĂ", en: "PREMIUM FLEET & STRICT MAINTENANCE" })}
              </h3>
              <p className="mt-3 max-w-2xl text-slate-400">
                {t({ ro: "Flota noastră este oglinda respectului pe care îl purtăm fiecărui transport: curată, modernă și inspectată tehnic la cele mai înalte standarde europene, pregătită pentru orice drum.", en: "Our fleet reflects the respect we give to every transport: clean, modern and technically inspected to the highest European standards, ready for any road." })}
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {stats.map((s) => (
                <div key={s.v} className="rounded-2xl border border-white/10 bg-slate-950/70 px-3 py-4 text-center">
                  <div className="font-display text-lg md:text-xl font-extrabold text-sky-400">{s.v}</div>
                  <div className="mt-1 text-[10px] md:text-[11px] uppercase tracking-wide text-slate-400 leading-tight">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        {/* Two tech cards */}
        <div className="mt-6 grid lg:grid-cols-2 gap-5">
          <Reveal delay={0.05}>
            <TechCard
              tid="tech-card-trucks"
              icon={CheckCircle2}
              title={t({ ro: "CAMIOANE STANDARD", en: "STANDARD TRUCKS" })}
              sub={t({ ro: "Echipate corespunzător", en: "Properly equipped" })}
              specs={[
                { icon: Leaf, t: t({ ro: "Motorizare EURO6", en: "EURO6 engine" }), d: t({ ro: "Tehnologie de ultimă generație", en: "State-of-the-art technology" }) },
                { icon: Wrench, t: t({ ro: "Verificări PERIODICE", en: "PERIODIC inspections" }), d: t({ ro: "Mentenanță tehnică riguroasă", en: "Strict technical maintenance" }) },
              ]}
              brands={STD_BRANDS}
            />
          </Reveal>
          <Reveal delay={0.1}>
            <TechCard
              tid="tech-card-trailers"
              icon={CheckCircle2}
              title={t({ ro: "SEMI-REMORCI DIVERSE", en: "VARIOUS SEMI-TRAILERS" })}
              sub={t({ ro: "Semi-remorci Box & frigorifice", en: "Box & refrigerated semi-trailers" })}
              specs={[
                { icon: Lock, t: t({ ro: "Lacăte INTELIGENTE", en: "SMART locks" }), d: t({ ro: "Sistem de siguranță", en: "Security system" }) },
                { icon: ThermometerSnowflake, t: t({ ro: "Control TEMPERATURĂ", en: "TEMPERATURE control" }), d: t({ ro: "Monitorizare constantă", en: "Continuous monitoring" }) },
              ]}
              brands={TRL_BRANDS}
            />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ---------- Process Timeline ---------- */
const STEPS = [
  { icon: FileText, ro: "Cerere ofertă", en: "Quote request", dro: "Ne trimiți detaliile transportului, iar noi răspundem rapid cu o soluție personalizată.", den: "You send us the transport details and we respond quickly with a tailored solution." },
  { icon: Route, ro: "Planificare", en: "Planning", dro: "Optimizăm ruta și programul pentru eficiență maximă și zero întârzieri.", den: "We optimize the route and schedule for maximum efficiency and zero delays." },
  { icon: PackageCheck, ro: "Încărcare", en: "Loading", dro: "Fixăm și manipulăm marfa conform protocoalelor stricte de siguranță.", den: "We secure and handle cargo according to strict safety protocols." },
  { icon: MonitorDot, ro: "Monitorizare", en: "Monitoring", dro: "Urmărim transportul GPS în timp real, cu actualizări la fiecare pas.", den: "We track the shipment via GPS in real time, with updates every step." },
  { icon: MapPin, ro: "Livrare", en: "Delivery", dro: "Marfa ajunge la destinație intactă, la timp, întotdeauna.", den: "Cargo arrives at its destination intact, on time, always." },
];

export function Process() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 65%", "end 55%"] });
  const h = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section id="proces" className="relative py-24 md:py-32 bg-slate-950">
      <div className="mx-auto max-w-5xl px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <SectionLabel>{t({ ro: "Proces de lucru", en: "How we work" })}</SectionLabel>
          <Reveal>
            <h2 className="mt-5 font-display text-4xl md:text-5xl font-extrabold tracking-tighter">
              {t({ ro: "Cinci pași până la destinație", en: "Five steps to destination" })}
            </h2>
          </Reveal>
        </div>

        <div ref={ref} className="relative mt-16 pl-10 md:pl-0">
          <div className="absolute left-[19px] md:left-1/2 top-0 bottom-0 w-px bg-white/10 md:-translate-x-1/2" />
          <motion.div style={{ height: h }} className="absolute left-[19px] md:left-1/2 top-0 w-px bg-gradient-to-b from-sky-400 to-blue-600 md:-translate-x-1/2" />

          <div className="space-y-10 md:space-y-16">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const left = i % 2 === 0;
              return (
                <Reveal key={i} delay={0.05} className="relative md:grid md:grid-cols-2 md:gap-12 items-center">
                  <div className={`${left ? "md:order-1 md:text-right" : "md:order-2"} `}>
                    <div data-testid={`process-step-${i}`} className="rounded-2xl border border-white/10 bg-slate-900/60 p-6 hover:border-sky-400/40 transition-colors">
                      <span className="font-display text-sky-400 text-sm font-bold tracking-widest">0{i + 1}</span>
                      <h3 className="mt-1 font-display text-2xl font-bold">{t(s)}</h3>
                      <p className="mt-2 text-slate-400">{t({ ro: s.dro, en: s.den })}</p>
                    </div>
                  </div>
                  <div className={`${left ? "md:order-2" : "md:order-1"} hidden md:block`} />
                  <div className="absolute left-[-21px] md:left-1/2 top-6 md:top-1/2 grid h-10 w-10 place-items-center rounded-full bg-slate-950 border-2 border-sky-400 md:-translate-x-1/2 md:-translate-y-1/2 z-10">
                    <Icon className="h-4 w-4 text-sky-400" />
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
